import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Handle Google OAuth credential response from @react-oauth/google
 * Sends the credential (ID token) to backend for verification
 * @param {string} credential - The Google ID token from frontend OAuth
 * @returns {Promise<Object>} Response with user data or registration requirement
 */
export const handleGoogleCredential = async (credential) => {
  try {
    const response = await axios.post(`${API}/auth/google/verify`, {
      credential: credential
    }, {
      withCredentials: true
    });
    
    return response.data;
  } catch (error) {
    console.error('Failed to verify Google credential:', error);
    throw error;
  }
};

/**
 * Complete Google OAuth registration with profile info
 * @param {string} tempToken - Temporary token from initial verification
 * @param {string} firstName
 * @param {string} lastName
 * @param {string} role - "project_manager" or "resource_manager"
 * @param {string} companyName
 * @param {string} city
 * @param {string} country
 * @returns {Promise<Object>} Response with user and token
 */
export const completeGoogleRegistration = async (tempToken, firstName, lastName, role, companyName, city, country) => {
  try {
    const response = await axios.post(`${API}/auth/google/complete`, {
      temp_token: tempToken,
      first_name: firstName,
      last_name: lastName,
      role: role,
      company_name: companyName,
      city: city,
      country: country,
      company_type: 'partner'
    }, {
      withCredentials: true
    });
    
    return response.data;
  } catch (error) {
    console.error('Failed to complete Google registration:', error);
    throw error;
  }
};

/**
 * Check if user has an existing session (from cookie)
 * @returns {Promise<boolean>}
 */
export const checkExistingSession = async () => {
  try {
    const response = await axios.get(`${API}/auth/me`, {
      withCredentials: true
    });
    return response.data ? true : false;
  } catch (error) {
    return false;
  }
};

/**
 * Logout user (revoke session and clear cookie)
 * @returns {Promise<void>}
 */
export const googleLogout = async () => {
  try {
    const token = localStorage.getItem('token');
    await axios.post(`${API}/auth/logout`, {}, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true
    });
  } catch (error) {
    console.error('Failed to logout:', error);
    throw error;
  }
};

// Legacy functions - kept for backward compatibility but no longer used
export const initiateGoogleAuth = async () => {
  console.warn('initiateGoogleAuth is deprecated. Use GoogleLogin component from @react-oauth/google');
};

export const getGoogleAuthStatus = () => {
  return null;
};

export const clearGoogleAuthParams = () => {
  // No longer needed as OAuth happens in popup
};
